import { route } from 'quasar/wrappers'
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory, isNavigationFailure,
  RouteLocationNormalized
} from 'vue-router'

import routes, {
  ROUTE_NAME_ACCOUNTS,
  ROUTE_NAME_HOME,
  ROUTE_NAME_LOGIN,
  ROUTE_NAME_PURCHASE,
  ROUTE_NAME_REGISTER,
  routeRequiresAuthorization
} from './routes'
import { Platform, QNotifyCreateOptions } from 'quasar'
import { useAuthUserStore } from 'stores/auth-user-store'
import { useHistoryStore } from 'stores/history-store'
import { axiosRequestManager } from 'boot/axios'
import permissionService, { SubscriptionEnum } from 'src/services/PermissionService'
import { ActionType } from 'src/services/AxiosRequestManager'
import * as Sentry from '@sentry/vue'
import { timeRangeStore } from 'stores/time-range-store'
import { useNotificationService } from 'src/services/useNotificationService'

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : (process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

  const router = createRouter({
    scrollBehavior: () => {
      return new Promise((resolve) => {
        resolve({ left: 0, top: 0, behavior: 'smooth' })
      })
    },
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE)
  })

  const notify = (notificationOptions: QNotifyCreateOptions | string, origin?: string[]): void => {
    // We have to do this here because the Service is only present at runtime!
    const notificationService = useNotificationService()
    notificationService.notify(notificationOptions, origin)
  }

  const notifyError = (message: string, willTimeout: boolean = true): void => {
    const notificationOptions: QNotifyCreateOptions = {
      position: 'bottom-right',
      message,
      type: 'error',
      icon: 'spi-triangle-exclamation'
    }

    if (!willTimeout) {
      notificationOptions.timeout = 0
      notificationOptions.actions = [{
        label: 'Dismiss',
        color: 'white',
        handler: (): void => { /* ... */
        }
      }]
    }
    notify(notificationOptions)
  }

  // Add referrer to every request
  router.beforeEach(async (to, from) => {
    Object.defineProperty(
      document,
      'referrer',
      {
        configurable: true,
        value: from.fullPath ? window.location.origin + from.fullPath : ''
      }
    )
  })

  // Login guard
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthUserStore()
    if (authStore.isLoggedIn && !authStore.hasValidSubscription && routeRequiresAuthorization(String(to.name))) {
      next({ name: ROUTE_NAME_ACCOUNTS })
    } else if (routeRequiresAuthorization(String(to.name)) && !authStore.isLoggedIn) {
      next({ name: ROUTE_NAME_LOGIN, query: { redirect: to.fullPath } })
    } else if (authStore.isLoggedIn && [ROUTE_NAME_REGISTER, ROUTE_NAME_LOGIN].includes(String(to.name))) {
      next({ name: ROUTE_NAME_HOME })
    } else if (
      authStore.isLoggedIn &&
      authStore.user.subscription_type !== SubscriptionEnum.TRIAL_USER &&
      String(to.name) === ROUTE_NAME_PURCHASE
    ) {
      next({ name: ROUTE_NAME_HOME })
    } else if (String(to.name).includes('trend_radar')) {
      if (!permissionService.isTagAvailable('trendradar')) {
        next({ name: ROUTE_NAME_HOME })
      } else {
        next()
      }
    } else if (String(to.name).includes('chat')) {
      if (!permissionService.isAiChatPermitted()) {
        next({ name: ROUTE_NAME_HOME })
      } else {
        next()
      }
    } else {
      next()
    }
  })

  // Time range selection guard
  router.beforeEach(async (to, from, next) => {
    if (!to.query) {
      next()
      return
    }

    const currentLocation = { ...to }
    // TODO refactor
    const isTrendRadarPage = String(currentLocation.name).includes('trend_radar')
    if (isTrendRadarPage) {
      next()
      return
    }

    const relativeTimeRangeQuery = to.query.relativeTimeRange
    const startDateQuery = to.query.startDate
    const endDateQuery = to.query.endDate

    if (!startDateQuery && !endDateQuery && !relativeTimeRangeQuery) {
      next()
      return
    }

    // We prioritize relativeTimeRange
    if (relativeTimeRangeQuery) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate

      const timeRange = timeRangeStore()
      const timeRangeIndex = timeRange.getRelativeTimeRangeOptionIndexByName(relativeTimeRangeQuery as string)

      // Check if TimeRangeOption is valid
      if (timeRangeIndex === -1) {
        notifyError('Removed invalid time range selection.')
        delete currentLocation.query.relativeTimeRange
        next(currentLocation)
        return
      }
      const selectedTimeRange = timeRange.relativeTimeRangeOptions[timeRangeIndex]
      // Check permission of selected TimeRangeOption
      if (!permissionService.isRelativeTimeRangePermitted(selectedTimeRange)) {
        notifyError(
          'The selected time range exceeds your permitted access and has been reset. If you need access to this selection, consider upgrading your plan.',
          false
        )
        delete currentLocation.query.relativeTimeRange
        next(currentLocation)
        return
      }

      // Only set new location if startDate or endDate is defined to prevent infinite loops
      if (startDateQuery || endDateQuery) {
        next(currentLocation)
      } else {
        next()
      }
      return
    }

    // We expect startDate and endDate to be present
    if (!startDateQuery || !endDateQuery) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate
      next(currentLocation)
      return
    }

    const startDateInt = parseInt(startDateQuery as string)
    const endDateInt = parseInt(endDateQuery as string)

    // Check for invalid params
    if (isNaN(startDateInt) || isNaN(endDateInt)) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate
      notifyError('The selected time range is invalid and has been reset.')

      next(currentLocation)
      return
    }

    // Check for startDate after endDate
    if (startDateInt > endDateInt) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate
      notifyError('The selected time range has been reset. The start date cannot be later than the end date')
      next(currentLocation)
      return
    }

    const authStore = useAuthUserStore()
    if (!authStore.isLoggedIn || !authStore.hasValidSubscription) {
      next()
    }

    // Check for permissions
    if (startDateInt < permissionService.getFirstDay().getTime()) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate
      notifyError(
        'The selected time range exceeds your permitted access and has been reset. If you need access to this selection, consider upgrading your plan.',
        false
      )
      next(currentLocation)
      return
    }
    const startDate = new Date(startDateInt)
    const endDate = new Date(endDateInt)
    if (!permissionService.isTimeRangePermittedByTimeSpanDays(startDate, endDate)) {
      delete currentLocation.query.startDate
      delete currentLocation.query.endDate
      notifyError(
        'The selected time range exceeds your permitted access and has been reset. If you need access to this selection, consider upgrading your plan.',
        false
      )
      next(currentLocation)
      return
    }

    next()
  })

  // collect navigation history
  router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized, failure) => {
    if (isNavigationFailure(failure)) {
      return
    }

    if (to.name) {
      const historyStore = useHistoryStore()
      historyStore.addHistoryEntry(to)
    }
  })

  router.afterEach((to, from, failure) => {
    if (failure && !isNavigationFailure(failure)) {
      console.error('Router navigation failure', failure)
    }
  })

  router.beforeEach((to, from) => {
    if (from.name !== ROUTE_NAME_LOGIN && from.name !== to.name) {
      axiosRequestManager.cancelRequests(ActionType.ChangePage)
    }
  })

  router.afterEach(async (to) => {
    if (Platform.within.iframe) {
      // TODO: remove? We do not need iframe
      // TODO: Restrict to https://stockpulse.ai
      // const targetOrigin = 'https://stockpulse.ai'
      const targetOrigin = '*'

      window.parent.postMessage({ type: 'navigation', data: { to: to.fullPath } }, targetOrigin)
    }
  })

  // Try to fix dynamically imported module: https://github.com/vitejs/vite/issues/11804
  router.onError((error, to) => {
    if (error.message.includes('error loading dynamically imported module')) {
      Sentry.captureException(error)
      if (!to?.fullPath) {
        window.location.reload()
      } else {
        window.location.href = to.fullPath
      }
    }
  })

  return router
})
